import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import DateCurrentInput from "../inputs/DateCurrentInput";
import RnReclamationRequestModal from "./RnReclamationRequestModal";
import RnQpRequestModal from "./RnQpRequestModal";
import OSCard, { OS_READONLY_CLS, OS_PLAIN_DISABLED_CLS, OS_PLAIN_DISABLED_SELECT_CLS, OS_CARD_HEADER_BUTTON_CLS } from "./OSCard";

interface DetailDataCardProps {
  mode: "os" | "rn";
  isEditable: boolean;
  productionCode: string;
  onProductionCodeChange: (value: string) => void;
  statusFields?: { _id: string; code?: string; name: string }[];
  statusValue?: string | null;
  onStatusChange?: (id: string) => void;
  versionDisplay?: string;
  validFrom?: string;
  onValidFromChange: (value: string) => void;
  validTo?: string;
  onValidToChange: (value: string) => void;
  // Подготвил/Одобрил rows — DetailDataSignatureRows, mode="os" or mode="rn" (both render here)
  signature?: React.ReactNode;
  actionsSlot?: React.ReactNode;
  rnWorkOrderId?: string;
  rnWorkOrderNumber?: string;
  /** RN operator view: no Рек/QP, shortened read-only table (РН, Идент, Серија, Статус, Датум). */
  operatorView?: boolean;
  productLabel?: string;
  batchLabel?: string;
  dateLabel?: string;
}

const thCls = "border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-left text-[11px] h-[26px] px-2";

// Canonical blue focus accent (CLAUDE.md hard rule — the yellow variant these carried is the
// known accent bug, not a style choice).
const cellInputCls = "w-full h-6 px-1 border-0 outline-none text-[11px] bg-transparent hover:bg-blue-50 focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50";
// Датум во ќелија — без ×, син фокус; лево порамнет како другите вредности во оваа картичка (Верзија…).
const dateCellInputCls = `w-full h-6 px-1 text-[11px] text-left border-0 outline-none bg-transparent focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 ${OS_PLAIN_DISABLED_CLS}`;

const DetailDataCard: React.FC<DetailDataCardProps> = ({
  mode,
  isEditable,
  productionCode,
  onProductionCodeChange,
  statusFields,
  statusValue,
  onStatusChange,
  versionDisplay = "001",
  validFrom,
  onValidFromChange,
  validTo,
  onValidToChange,
  signature,
  actionsSlot,
  rnWorkOrderId,
  rnWorkOrderNumber,
  operatorView = false,
  productLabel,
  batchLabel,
  dateLabel,
}) => {
  const { t } = useTranslation();
  const showStatusRow = !!(statusFields && statusFields.length > 0);
  const [reclamationOpen, setReclamationOpen] = useState(false);
  const [qpRequestOpen, setQpRequestOpen] = useState(false);
  const canOpenReclamation = mode === "rn" && !!rnWorkOrderId;
  const showRequestButtons = mode === "rn" && !operatorView;

  const readOnlyRow = (label: string, value?: string) => (
    <tr>
      <th className={`${thCls} w-28`}>{label}</th>
      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
        <input
          type="text"
          value={value || ""}
          readOnly
          disabled
          className={`${cellInputCls} ${OS_READONLY_CLS}`}
        />
      </td>
    </tr>
  );

  const validFromRow = (
    <tr>
      <th className="border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-left text-[11px] h-[26px] px-2">Важи од</th>
      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
        <DateCurrentInput
          value={validFrom || ""}
          onChange={onValidFromChange}
          disabled={!isEditable || mode === "rn"}
          clearable={false}
          inputClassName={dateCellInputCls}
        />
      </td>
    </tr>
  );

  const validToRow = (
    <tr>
      <th className="border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-left text-[11px] h-[26px] px-2">Важи до</th>
      <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
        <DateCurrentInput
          value={validTo || ""}
          onChange={onValidToChange}
          disabled={!isEditable || mode === "rn"}
          clearable={false}
          inputClassName={dateCellInputCls}
        />
      </td>
    </tr>
  );

  return (
    <OSCard
      // РН: без наслов — зелената лента ги носи кратките копчиња (целото име е во tooltip).
      title={mode === "rn" ? "" : "Податоци"}
      actions={mode === "rn" && (
        <>
          {showRequestButtons && (
            <>
              <button
                type="button"
                onClick={() => setReclamationOpen(true)}
                disabled={!canOpenReclamation}
                title={canOpenReclamation ? t("rnReclamationRequests.openButton") : t("rnReclamationRequests.saveFirst")}
                className={OS_CARD_HEADER_BUTTON_CLS}
              >
                Рек
              </button>
              <button
                type="button"
                onClick={() => setQpRequestOpen(true)}
                disabled={!canOpenReclamation}
                title={canOpenReclamation ? t("rnQpRequests.openButton") : t("rnQpRequests.saveFirst")}
                className={OS_CARD_HEADER_BUTTON_CLS}
              >
                QP
              </button>
            </>
          )}
          {actionsSlot}
        </>
      )}
    >
      <div className="overflow-x-auto">
        {/* Same card as /os: label column is `w-28` (card sits in col-span-3). */}
        <table className="border-collapse w-full table-fixed">
          <tbody>
            {operatorView && readOnlyRow("Бр. на РН", rnWorkOrderNumber)}
            {operatorView && readOnlyRow("Идент", productLabel)}
            {operatorView && readOnlyRow("Серија", batchLabel)}
            {showStatusRow && (
              <tr>
                <th className="border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-left text-[11px] h-[26px] px-2 w-28">{t("tableHeaders.statusLabel")}</th>
                <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                  <select
                    value={statusValue || ""}
                    onChange={(e) => onStatusChange?.(e.target.value)}
                    disabled={!isEditable || operatorView}
                    className={`${cellInputCls} ${OS_PLAIN_DISABLED_SELECT_CLS}`}
                  >
                    {statusFields?.map((status) => (
                      <option key={status._id} value={status._id}>
                        {status.name}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            )}
            {operatorView && readOnlyRow("Датум", dateLabel)}
            {!operatorView && (
              <>
                <tr>
                  <th
                    className="border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-left text-[11px] h-[26px] px-2 w-28"
                    title="Шифра на Тех. Пос."
                  >
                    Ш. Тех. Пос.
                  </th>
                  <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                    <input
                      type="text"
                      value={productionCode}
                      onChange={(e) => onProductionCodeChange(e.target.value)}
                      disabled={!isEditable || mode === "rn"}
                      className={`${cellInputCls} ${mode === "rn" ? OS_READONLY_CLS : OS_PLAIN_DISABLED_CLS}`}
                    />
                  </td>
                </tr>
                <tr>
                  <th className="border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-left text-[11px] h-[26px] px-2">Верзија</th>
                  <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                    <input
                      type="text"
                      value={versionDisplay}
                      readOnly
                      disabled
                      className={`${cellInputCls} ${OS_READONLY_CLS}`}
                    />
                  </td>
                </tr>

                {validFromRow}
                {validToRow}
                {signature}
              </>
            )}
          </tbody>
        </table>
      </div>

      {showRequestButtons && (
        <RnReclamationRequestModal
          isOpen={reclamationOpen}
          onClose={() => setReclamationOpen(false)}
          rnWorkOrderId={rnWorkOrderId || ""}
          rnWorkOrderNumber={rnWorkOrderNumber || ""}
        />
      )}
      {showRequestButtons && <RnQpRequestModal isOpen={qpRequestOpen} onClose={() => setQpRequestOpen(false)} rnWorkOrderId={rnWorkOrderId || ""} rnWorkOrderNumber={rnWorkOrderNumber || ""} />}
    </OSCard>
  );
};

export default DetailDataCard;
